import {useMemo, useState, useRef, useEffect, useCallback} from 'react';
import OverlayTrigger from 'react-bootstrap/OverlayTrigger';
import Tooltip from 'react-bootstrap/Tooltip';
import {useDispatch, useSelector} from 'react-redux';
import {applicationSelector, updateDesign} from '../../../slices/application';
import {useShadowRoot} from '../../../contexts/ShadowRootContext';

const Color = ({
  choices,
  compatibilityPath,
  enabled = false,
  handleSelect,
  label,
  required = false,
  showIncompatible = true
}) => {
  const dispatch = useDispatch();
  const {design, heatshrinkCompatibility} = useSelector(applicationSelector);
  const {mostRecentSelectedTubingAttribute} = design;
  const [show, setShow] = useState(false);
  const dropdownRef = useRef(null);
  const shadowRoot = useShadowRoot();

  const handleToggle = useCallback(() => {
    setShow(prev => !prev);
  }, []);

  const handleClose = useCallback(() => {
    setShow(false);
    dispatch(updateDesign({}));
  }, [dispatch]);

  // Custom click-outside detection for Shadow DOM
  useEffect(() => {
    if (!show) return;

    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setShow(false);
        dispatch(updateDesign({}));
      }
    };

    // Use requestAnimationFrame to ensure we don't catch the opening click
    const rafId = requestAnimationFrame(() => {
      shadowRoot.addEventListener?.('mousedown', handleClickOutside);
      document.addEventListener('mousedown', handleClickOutside, true);
    });
    
    return () => {
      cancelAnimationFrame(rafId);
      shadowRoot.removeEventListener?.('mousedown', handleClickOutside);
      document.removeEventListener('mousedown', handleClickOutside, true);
    };
  }, [show, shadowRoot, dispatch]);

  const designAttribute = useMemo(
    () =>
      compatibilityPath === '/heatshrinkColor'
        ? design.heatshrinkColor
        : (design.printColor ?? 'Red'),
    [compatibilityPath, design.heatshrinkColor, design.printColor]
  );

  const selectedObject = useMemo(
    () => choices.filter((obj) => obj.value === designAttribute),
    [choices, designAttribute]
  );

  function colorIncompatible(colorselected) {
    const check = heatshrinkCompatibility['printColor'].find((x) => x.value == colorselected);
    return check ? !check.compatible : true;
  }

  function selectionClick(e) {
    if (colorIncompatible(e.target.innerText)) {
      alert('Sorry, that print color is incompatible with your heatshrink color');
      return;
    }

    dispatch(updateDesign({}));
    handleSelect(e);
  }

  function handleKeyDown(e) {
    if (e.key == 'Enter') {
      selectionClick(e);
      handleClose();
    }
  }

  return (
    <div
      id={`${compatibilityPath}`}
      ref={dropdownRef}
      className={`hs-form-control-group pt-0 ${
        mostRecentSelectedTubingAttribute !== compatibilityPath
          ? !choices.filter((obj) => {
              return obj.value === selectedObject[0]?.value;
            })[0]?.compatible
            ? 'has-error'
            : ''
          : ''
      }`}>
      <label htmlFor={label} className="form-label hs-form-label">
        {required ? '' : <em>Optional</em>}
        {label}
        <strong>Incompatible</strong>
      </label>
      <div className="dropdown hs-form-dropdown">
        <button
          type="button"
          className={`btn uix-btn-outline-default btn-sm input-radius dropdown-toggle hs-form-dropdown-toggle ${
            !selectedObject[0]?.compatible ? 'not-compatible' : ''
          }`}
          id={label}
          disabled={!enabled}
          onClick={handleToggle}
          title={selectedObject[0]?.value}>
          <span
            className={`badge hs-form-badge print-${selectedObject[0]?.value.toLowerCase()}`}></span>
          {selectedObject[0]?.value}
        </button>

        {/* TODO: Toggle 'show' class to hide/show menu */}
        <div
          style={{margin: 0}}
          className={`dropdown-menu hs-form-dropdown-menu ${show ? 'show' : ''}`}
          aria-labelledby={label}>
          {choices
            ? choices.map((choice) => {
                let tooltipMessage = 'Incompatible with ';
                if (choice.incompatible) {
                  const translation = {
                    shrinkRatio: 'ShrinkRatio',
                    finish: 'Finish',
                    diameter: 'Diameter',
                    heatshrinkColor: 'Color',
                    undefined: 'other selected options'
                  };
                  for (let i = 0; i < choice.incompatible.length - 1; i++) {
                    tooltipMessage += `${translation[choice.incompatible[i]]}, `;
                  }
                  tooltipMessage += `${
                    translation[choice.incompatible[choice.incompatible.length - 1]]
                  }`;
                }

                const overlayChoice =
                  !choice.compatible && showIncompatible ? (
                    <Tooltip>{tooltipMessage}</Tooltip>
                  ) : null;

                const dropdownItem = (
                  <button
                    type="button"
                    key={choice.value}
                    className={`dropdown-item ${
                      choice.value === selectedObject[0]?.value ? 'active' : ''
                    } ${!choice.compatible ? 'not-compatible' : ''}`}
                    onMouseDown={selectionClick}
                    onKeyDown={handleKeyDown}
                    title={choice.value}>
                    <span
                      className={`badge hs-form-badge print-${choice.value.toLowerCase()}`}></span>
                    {choice.value}
                  </button>
                );

                if (overlayChoice) {
                  return (
                    <OverlayTrigger trigger={['hover', 'focus']} placement="right" container={shadowRoot} overlay={overlayChoice} key={choice.value}>
                      {dropdownItem}
                    </OverlayTrigger>
                  );
                }
                return dropdownItem;
              })
            : null}
        </div>
      </div>
    </div>
  );
};

export default Color;
